import classNames from 'classnames';
import { Star } from 'components/Icons';
import React from 'react';
import ExperimentalFeatureDecorator from './account-features/ExperimentalFeatureDecorator';
import styles from './DropdownList.module.scss';

export type OptionTypes =
  | 'default'
  | 'header'
  | 'muted'
  | 'disabled'
  | 'danger'
  | 'completed'
  | 'divider'
  | 'info'
  | 'search';

interface Props extends Omit<React.HTMLAttributes<HTMLLIElement>, 'onClick'> {
  isActive?: boolean;
  isDisabled?: boolean;
  type?: OptionTypes;
  onClick?: (event: React.SyntheticEvent<HTMLElement>, props: Props) => void;
  onLockedFeatureClick?: () => void;
  isLockedFeature?: boolean;
  isExperimentalFeature?: boolean;
  clean?: boolean;
}

const DropDownListItem = React.forwardRef<HTMLLIElement, Props>((props, ref) => {
  const {
    children,
    type = 'default',
    isActive,
    isDisabled,
    isLockedFeature,
    isExperimentalFeature,
    className,
    clean,
    onClick,
    onLockedFeatureClick,
    ...rest
  } = props;

  const handleOnClick = (event: React.SyntheticEvent<HTMLElement>) => {
    // If there is a label with an input in it (for example a `<Checkbox>`) it will trigger a double
    // onClick, so this behavior needs to be disabled with `preventDefault()`.
    if ((event.target as HTMLElement)?.tagName === 'LABEL') {
      event.preventDefault();
    }

    if (isLockedFeature) {
      onLockedFeatureClick?.();
      return;
    }

    if (!isDisabled) {
      onClick?.(event, props);
    }
  };

  const containerClass = classNames(className, {
    [styles[type!]]: !!type,
    [styles.disabled]: isDisabled,
    [styles.active]: isActive,
    [styles.isLockedFeature]: isLockedFeature,
    [styles.clean]: clean,
    [styles.clickable]:
      (!isDisabled &&
        !isActive &&
        !!onClick &&
        !['divider', 'header', 'info', 'search'].includes(type)) ||
      !!onLockedFeatureClick,
  });

  return (
    <li
      data-testid="dropdown_list_item"
      className={containerClass}
      onClick={handleOnClick}
      ref={ref}
      {...rest}
    >
      {!!isLockedFeature && <Star />}
      {isExperimentalFeature ? (
        <ExperimentalFeatureDecorator
          tooltipStyle={{ width: 12, right: -16 }}
          message="Only visible to Memory employees"
        >
          {children}
        </ExperimentalFeatureDecorator>
      ) : (
        children
      )}
    </li>
  );
});

DropDownListItem.displayName = 'DropDownListItem';
export default DropDownListItem;
